import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useApp }     from '../../../../app/AppContext.jsx'
import { useHero }    from '../../context/HeroContext.jsx'
import { useCatalog } from '../../../catalog/index.js'

import { useSvgBuildingInteraction }  from '../../../../shared/hooks/useSvgBuildingInteraction.js'
import { useSvgTierInteraction }      from '../../../../shared/hooks/useSvgTierInteraction.js'
import { useSvgProductInteraction }   from '../../../../shared/hooks/useSvgProductInteraction.js'
import { isDisplayable }              from '../../../../shared/utils/productUtils.js'
import { 
  encodeColorModeLegendHoverValue,
  resolveColorModeHoveredLegendKey }  from '../../../catalog/utils/colorModeLegend.js' //move to shared
import { fetchLocalJSON }             from '../../../../shared/utils/fetchLocalJSON.js'
import { getBuildingNameByIdCad }     from '../../../../shared/utils/idCadUtils.js'
import { syncLegendHighlightDOM }     from '../../../../shared/utils/colorModeLegend.js'
import { AVAILABILITY_STATUS }        from '../../../../shared/constants/availabilityStatus.js'
import { COLOR_MODE }                 from '../../../../shared/constants/colorMode.js'
import { COLOR_MODE_REGISTRY }        from '../../../../shared/constants/colorMode.js'
import { HERO_VIEW }                  from '../../../../shared/constants/heroView.js'
import { HERO_OUTLINE }               from '../../../../shared/constants/heroOutline.js'

import { 
  useModelFrames, 
  wrapFrameIndex 
}                                     from '../../hooks/useModelFrames.js'
import { useHeroSectionNavigation }   from '../../hooks/useHeroSectionNavigation.js'
import { useOutlineSvgFetch }         from '../../hooks/useOutlineSvgFetch.js'
import { MODEL_FRAME_INTERVAL_MS }    from '../../constants/animationTimings.js'
import { buildModelProductCandidates } from '../../utils/modelPreviewAdapter.js'
import { buildModelOutlineCandidates } from '../../utils/outlinePreviewAdapter.js'


import { ModelControls }        from '../ModelControls/ModelControls.jsx'
import { SvgOutlineLayer }      from '../SvgOutlineLayer/SvgOutlineLayer.jsx'

import { LoadingFrames }    from '../../../../shared/ui/FramePlayer/LoadingFrames/LoadingFrames.jsx'
import { PreviewOverlay } from '../PreviewOverlay/PreviewOverlay.jsx' //move to shared/

import styles from './ModelView.module.scss'

// ── CSS class names used for hot-path classList toggling ───────────────────────
// Defined as module-independent strings so they can be applied both in SCSS
// (.frame--active) and imperatively in useModelFrames without importing styles.
const ACTIVE_FRAME_CLASS = 'frame--active'
const ACTIVE_SVG_CLASS   = 'svg-frame--active'

/** Minimum ms between frames for intro animation (~30 fps). */
const INTRO_FRAME_MS = MODEL_FRAME_INTERVAL_MS
const MODEL_ZOOM_MIN = 1
const MODEL_ZOOM_MAX = 2
const MODEL_ZOOM_STEP = 0.25

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * ModelView — 360° rotatable model frame player with SVG outline overlay.
 *
 * Renders all frames as `<img>` elements simultaneously in the DOM.
 * Active frame = only visible frame, toggled via `classList` (zero React setState
 * in the drag hot path). SVG `<g data-frame>` groups are toggled in sync
 * by `useModelFrames` via `frameGroupsRef`.
 *
 * Props:
 *   onClose  {() => void}  — called from the Close button (model → visualization)
 */
export function ModelView({ onClose }) {
  // ── Context ──────────────────────────────────────────────────────────────────

  const {
    appCapabilities,
    windowData,
    activeBuilding,
    activeTier,
    activeProduct,
    isMobile,
  } = useApp()

  const {  
    buildings,
    catalogCapabilities,
    tiers,
    products,
    filteredProducts,
    activeFiltersConfig,
    defaultFiltersConfig,
    setActiveFiltersConfig,
    activeFiltersCount,
    colorMode,
    setColorMode,
    availableColorModes,
    colorModeDeciles,
    openFiltersUi,
  } = useCatalog()

  const {
    heroCapabilities,
    activeView,
    changeViewHandler,
    activeOutline,
    setActiveOutline,
    isOutlineVisible,
    setIsOutlineVisible,
    availableOutlines,
  } = useHero()

  // ── Local state ───────────────────────────────────────────────────────────

  const svgOutlineContainerRef = useRef(null)
  const modelWrapperRef = useRef(null)
  const zoomPanContainerRef = useRef(null)
  const zoomRef = useRef(MODEL_ZOOM_MIN)
  const panRef = useRef({ x: 0, y: 0 })
  const panDragRef = useRef(null)
  const legendContainerRef = useRef(null)
  const previewHandlersRef = useRef({ handlePreviewChange: () => {}, handleClosePreview: () => {} })
  const executeActionRef = useRef(() => {})

  const handlePreviewChange = useCallback((data) => {
    previewHandlersRef.current.handlePreviewChange(data)
  }, [])

  const handleRegisterPreviewHandler = useCallback((handlers) => {
    previewHandlersRef.current = handlers
    executeActionRef.current = handlers.handleExecuteAction 
  }, [])

  const handleOutlineAction = useCallback((mode, data) => {
    executeActionRef.current(mode, data)
  }, [])

  const handleProductHover = useCallback((product) => {
    if (!legendContainerRef.current) return
    if (!product) {
      syncLegendHighlightDOM(legendContainerRef.current, null)
      return
    }

    const modeConfig = COLOR_MODE_REGISTRY[colorMode]
    const key = resolveColorModeHoveredLegendKey({
      hoveredProduct: product,
      colorMode,
      colorModeDeciles,
      modeConfig,
    })
    const hoverValue = encodeColorModeLegendHoverValue(colorMode, key)

    syncLegendHighlightDOM(legendContainerRef.current, hoverValue)
  }, [colorMode, colorModeDeciles])

  const handleLegendHoverChange = useCallback((value) => {
    const svgContainer = svgOutlineContainerRef.current
    if (!svgContainer) return

    if (value == null) {
      svgContainer.removeAttribute('data-legend-hover')
      return
    }

    svgContainer.setAttribute('data-legend-hover', String(value))
  }, [])

  useEffect(() => {
    if (activeOutline === HERO_OUTLINE.APARTMENTS) return
    handleLegendHoverChange(null)
  }, [activeOutline, handleLegendHoverChange])

  useEffect(() => {
    if (isOutlineVisible) return
    handleLegendHoverChange(null)
  }, [isOutlineVisible, handleLegendHoverChange])


  // ── Derived from context ─────────────────────────────────────────────────────

  const {
    areSoldOutBuildingsActive,
    areSoldOutTiersActive,
    hasMultipleBuildings
  } = catalogCapabilities;

  const { siteRenderGaussian } = heroCapabilities;

  const dataUrl           = windowData?.projectDataUrl ?? null
  const isSingleBuilding  = !hasMultipleBuildings
  const displayableProducts = useMemo(
    () => (products ?? []).filter(product => isDisplayable(product.visibility)),
    [products],
  )
  const visibleSoldProducts = useMemo(
    () => (filteredProducts ?? []).filter(product => product.status === AVAILABILITY_STATUS.SOLD),
    [filteredProducts],
  )
  const showSoldHighlightNote = heroCapabilities.highlightSoldInRed
    && colorMode !== COLOR_MODE.STATUS
    && visibleSoldProducts.length > 0
  const {
    reservedUnitsAccessible,
    soldUnitsAccessible,
  } = appCapabilities

  const areSoldAccessible = soldUnitsAccessible //TODO: should boolean constants start with has*, are*, is* ?
  const areReservedAccessible = reservedUnitsAccessible //TODO: should boolean constants start with has*, are*, is* ?

  const framesCount = activeView === HERO_VIEW.BUILDING
    ? heroCapabilities.buildingFramesCount
    : heroCapabilities.siteFramesCount

  const frameStart = activeView === HERO_VIEW.BUILDING
    ? heroCapabilities.buildingFrameStart
    : heroCapabilities.siteFrameStart

  // id_cad needed for BUILDING frame URLs and SVG fetch
  const singleBuildingIdCad = isSingleBuilding ? buildings[0]?.id_cad : null
  const activeBuildingIdCad = activeBuilding ?? singleBuildingIdCad
  const activeBuildingName = getBuildingNameByIdCad(activeBuildingIdCad, buildings)

  // ── Azimuth → compass north angle ──────────────────────────────────────────────
  // BUILDING scope: azimuth from buildings data.
  // SITE scope: azimuth from site/site.json → address.azimuth.

  const [siteAzimuth, setSiteAzimuth] = useState(null)

  useEffect(() => {
    if (!dataUrl) return
    let cancelled = false
    const base = dataUrl.endsWith('/') ? dataUrl : dataUrl + '/'
    fetchLocalJSON(`${base}site/site.json`, 'ModelView: site.json')
      .then((data) => {
        if (!cancelled && data?.address?.azimuth != null) {
          setSiteAzimuth(data.address.azimuth)
        }
      })
    return () => { cancelled = true }
  }, [dataUrl])

  const activeAzimuth = activeView === HERO_VIEW.BUILDING
    ? (buildings.find(b => b.id_cad === activeBuildingIdCad)?.azimuth ?? 90)
    : (siteAzimuth ?? 90)

  // Defined here (before useModelFrames) so it can be passed as onDragStart.
  const handleDragStart = useCallback(() => {
    previewHandlersRef.current.handleClosePreview()
  }, [])

  const handleShowFilters = useCallback(() => {
    openFiltersUi?.()
  }, [openFiltersUi])

  // Zoom/pan is deliberately DOM-driven. Updating these refs never renders
  // ModelView, so the SVG interaction listeners remain attached.
  const clampPan = useCallback((pan, scale = zoomRef.current) => {
    const viewport = modelWrapperRef.current
    if (!viewport || scale <= MODEL_ZOOM_MIN) return { x: 0, y: 0 }

    const maxX = viewport.clientWidth * (scale - 1) / 2
    const maxY = viewport.clientHeight * (scale - 1) / 2
    return {
      x: Math.max(-maxX, Math.min(maxX, pan.x)),
      y: Math.max(-maxY, Math.min(maxY, pan.y)),
    }
  }, [])

  const applyZoomPanTransform = useCallback(({ animate = false } = {}) => {
    const container = zoomPanContainerRef.current
    const viewport = modelWrapperRef.current
    if (!container) return

    const { x, y } = panRef.current
    container.style.transition = animate ? 'transform 180ms ease-out' : 'none'
    container.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${zoomRef.current})`
    if (viewport) {
      viewport.style.touchAction = zoomRef.current > MODEL_ZOOM_MIN ? 'none' : 'pan-y'
      viewport.dataset.panMode = zoomRef.current > MODEL_ZOOM_MIN ? 'true' : 'false'
    }
  }, [])

  const changeZoom = useCallback((direction) => {
    const nextZoom = Math.max(
      MODEL_ZOOM_MIN,
      Math.min(MODEL_ZOOM_MAX, zoomRef.current + direction * MODEL_ZOOM_STEP),
    )
    zoomRef.current = nextZoom
    panRef.current = clampPan(panRef.current, nextZoom)
    previewHandlersRef.current.handleClosePreview()
    applyZoomPanTransform({ animate: true })
    return nextZoom
  }, [applyZoomPanTransform, clampPan])

  const handleZoomIn = useCallback(() => changeZoom(1), [changeZoom])
  const handleZoomOut = useCallback(() => changeZoom(-1), [changeZoom])

  // ── Intro animation setup (before useModelFrames) ─────────────────────────
  // If this is the first visit for this project in this session, the model
  // should start at introStartFrame (¼ rotation before frameStart) so the
  // loading blur shows that frame and the post-load animation sweeps forward
  // to frameStart. On revisits, start directly at frameStart.

  const projectId = windowData?.projectId ?? null

  const introStartFrame = useMemo(() => {
    if (!projectId) return null
    const storageKey = `bim-hero-intro:${projectId}`
    if (sessionStorage.getItem(storageKey)) return null
    return wrapFrameIndex(frameStart - Math.floor(framesCount / 4), framesCount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionally empty — compute once on mount

  // First visible frame must match loading preview to avoid visual jump
  // when the loading overlay disappears.
  const initialVisibleFrame = introStartFrame ?? frameStart

  // Render priority frame first in DOM so browser starts fetching it earliest.
  const frameRenderOrder = useMemo(() => {
    const order = [initialVisibleFrame]
    for (let i = 0; i < framesCount; i += 1) {
      if (i !== initialVisibleFrame) order.push(i)
    }
    return order
  }, [framesCount, initialVisibleFrame])


  // ── SVG outline fetch ─────────────────────────────────────────────────────────

  const { svgString, svgStatus } = useOutlineSvgFetch({
    dataUrl,
    activeView,
    activeOutline,
    activeBuildingIdCad,
  })


  // ── Frame player ─────────────────────────────────────────────────────────────

  const {
    frameImgRefsRef,
    frameGroupsRef,
    currentIndexRef,
    isDraggingRef,
    getFrameUrl,
    loadingProgress,
    isLoaded,
    compassElRef,
    onImgLoad,
    handleFrameChange,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleRotateLeft,
    handleRotateRight,
    handleGoToNorth,
  } = useModelFrames({
    dataUrl,
    activeView,
    activeBuilding: activeBuildingIdCad,
    framesCount,
    frameStart,
    initialFrame:   introStartFrame ?? undefined,
    azimuth:        activeAzimuth,
    activeFrameClass: ACTIVE_FRAME_CLASS,
    activeSvgClass:   ACTIVE_SVG_CLASS,
    onDragStart:      handleDragStart,
  })

  const currentFrameForScope = wrapFrameIndex(currentIndexRef.current ?? 0, framesCount)

  const handleViewportMouseDown = useCallback((event) => {
    if (zoomRef.current <= MODEL_ZOOM_MIN) {
      handleMouseDown(event)
      return
    }
    isDraggingRef.current = true
    panDragRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
    }
    handleDragStart()
  }, [handleDragStart, handleMouseDown, isDraggingRef])

  const handleViewportMouseMove = useCallback((event) => {
    if (zoomRef.current <= MODEL_ZOOM_MIN) {
      handleMouseMove(event)
      return
    }
    const start = panDragRef.current
    if (!start) return
    panRef.current = clampPan({
      x: start.panX + event.clientX - start.clientX,
      y: start.panY + event.clientY - start.clientY,
    })
    applyZoomPanTransform()
  }, [applyZoomPanTransform, clampPan, handleMouseMove])

  const handleViewportMouseUp = useCallback(() => {
    if (panDragRef.current) {
      panDragRef.current = null
      isDraggingRef.current = false
      return
    }
    handleMouseUp()
  }, [handleMouseUp, isDraggingRef])

  const handleViewportTouchStart = useCallback((event) => {
    if (zoomRef.current <= MODEL_ZOOM_MIN) {
      handleTouchStart(event)
      return
    }
    if (event.touches.length !== 1) return
    const touch = event.touches[0]
    isDraggingRef.current = true
    panDragRef.current = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
    }
    handleDragStart()
  }, [handleDragStart, handleTouchStart, isDraggingRef])

  const handleViewportTouchMove = useCallback((event) => {
    if (zoomRef.current <= MODEL_ZOOM_MIN) {
      handleTouchMove(event)
      return
    }
    const start = panDragRef.current
    if (!start || event.touches.length !== 1) return
    const touch = event.touches[0]
    panRef.current = clampPan({
      x: start.panX + touch.clientX - start.clientX,
      y: start.panY + touch.clientY - start.clientY,
    })
    applyZoomPanTransform()
  }, [applyZoomPanTransform, clampPan, handleTouchMove])

  const handleViewportTouchEnd = useCallback(() => {
    if (panDragRef.current) {
      panDragRef.current = null
      isDraggingRef.current = false
      return
    }
    handleTouchEnd()
  }, [handleTouchEnd, isDraggingRef])

  useEffect(() => {
    const handleResize = () => {
      panRef.current = clampPan(panRef.current)
      applyZoomPanTransform()
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [applyZoomPanTransform, clampPan])

  // ── First-visit intro rotation (introStartFrame → frameStart) ─────────────
  //
  // On the very first time ModelView loads for this project session, animate
  // a quarter-rotation forward from introStartFrame to frameStart so the user
  // sees the model can rotate. Uses sessionStorage keyed by projectId to fire
  // only once per browser tab.

  const introFiredRef = useRef(false)

  useEffect(() => {
    if (!isLoaded || introFiredRef.current) return
    if (introStartFrame == null) return

    introFiredRef.current = true
    sessionStorage.setItem(`bim-hero-intro:${projectId}`, '1')

    // Nothing to animate if already at frameStart (or no distance to cover).
    if (currentIndexRef.current === frameStart) return

    let frame = currentIndexRef.current
    let lastTs = 0
    let rafId

    const step = (now) => {
      if (now - lastTs >= INTRO_FRAME_MS) {
        frame = wrapFrameIndex(frame + 1, framesCount)
        handleFrameChange(frame)
        lastTs = now
        if (frame === frameStart) return
      }
      rafId = requestAnimationFrame(step)
    }
    rafId = requestAnimationFrame(step)

    return () => cancelAnimationFrame(rafId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded])


  // ── Outline mobile carousel candidates (buildings / tiers / products mobile swipe) ────────────────────

  const getMobileOutlineCandidates = useCallback(() => buildModelOutlineCandidates({
    containerEl: svgOutlineContainerRef.current,
    currentFrameIndex: currentIndexRef.current,
    activeOutline,
    buildings: buildings ?? [],
    tiers: tiers ?? [],
  }), [activeOutline, buildings, tiers, currentIndexRef])

  const getMobileProductCandidates = useCallback(() => buildModelProductCandidates({
    containerEl: svgOutlineContainerRef.current,
    currentFrameIndex: currentIndexRef.current,
    products: products ?? [],
  }), [products, currentIndexRef])

  
  // ── Navigation handlers ──────────────────────────────────────────────────────
  
  const {
    goToBuildingModel,
    goToTierSection, //why from useHero isn't this normal app navigation? TODO: check!
  } = useHeroSectionNavigation({
    products,
    onAfterNavigate:     previewHandlersRef.current.handleClosePreview,
  })

  const handleBack = useCallback(() => {
    changeViewHandler(HERO_VIEW.SITE)
  }, [changeViewHandler])


  // ── Interaction hooks ─────────────────────────────────────────────────────────

  const deviceLayout = useMemo(() => ({ isMobile }), [isMobile]);

  const { wireListeners: buildingWireListeners } = useSvgBuildingInteraction({
    areSoldOutBuildingsActive,
    buildingsData:   buildings ?? [],
    activeBuilding:  activeBuildingIdCad,
    onOutlineClick:   handleOutlineAction,
    onPreviewChange:  handlePreviewChange,
    currentFrameRef: currentIndexRef,
    isDraggingRef,
    deviceLayout,
    soldUnitsAccessible,
  })
  
  const { wireListeners: tierWireListeners } = useSvgTierInteraction({
    areSoldOutBuildingsActive,
    areSoldOutTiersActive,
    tiersData:     tiers ?? [],
    buildingsData: buildings ?? [],
    activeTier,
    activeBuilding: activeBuildingIdCad,
    onOutlineClick:   handleOutlineAction,
    onPreviewChange: handlePreviewChange,
    currentFrameRef: currentIndexRef,
    isDraggingRef,
    deviceLayout,
    soldUnitsAccessible,
  })

  const { wireListeners: productWireListeners } = useSvgProductInteraction({
    products:          products ?? [],
    filteredProducts:  filteredProducts ?? [],
    buildings:         buildings ?? [],
    activeProduct,
    colorMode,
    colorModeDeciles,
    onProductClick:    handleOutlineAction,
    onPreviewChange:   handlePreviewChange,
    onProductHover:    handleProductHover,
    areSoldAccessible,
    areReservedAccessible,
    highlightSoldInRed: heroCapabilities.highlightSoldInRed,
    currentFrameRef:   currentIndexRef,
    isDraggingRef,
    deviceLayout,
  })

  // Pick the right wireListeners for the active outline
  const wireListeners =
    activeOutline === HERO_OUTLINE.BUILDINGS  ? buildingWireListeners :
    activeOutline === HERO_OUTLINE.TIERS      ? tierWireListeners     :
    activeOutline === HERO_OUTLINE.APARTMENTS ? productWireListeners  :
    null


  // ── Render ────────────────────────────────────────────────────────────────────────────────────────────────────────

  return (
    <div
      ref={modelWrapperRef}
      className={[styles.modelWrapper, !isLoaded ? styles.loading : ''].join(' ').trim()}
      onMouseDown={handleViewportMouseDown}
      onMouseMove={handleViewportMouseMove}
      onMouseUp={handleViewportMouseUp}
      onMouseLeave={handleViewportMouseUp}
      onTouchStart={handleViewportTouchStart}
      onTouchMove={handleViewportTouchMove}
      onTouchEnd={handleViewportTouchEnd}
      onTouchCancel={handleViewportTouchEnd}
    >
      <div ref={zoomPanContainerRef} className={styles.zoomPanContainer}>
      {/* ── Frame images ─────────────────────────────────────────────────── */}
      <div className={styles.framesHolder}>
        {frameRenderOrder.map((i) => (
          <img
            key={i}
            ref={(el) => { frameImgRefsRef.current[i] = el }}
            src={getFrameUrl(i)}
            alt=""
            loading="eager"
            decoding="async"
            fetchPriority={i === initialVisibleFrame || i === frameStart ? 'high' : 'low'}
            draggable={false}
            onLoad={onImgLoad}
            className={[styles.frame, i === currentFrameForScope ? ACTIVE_FRAME_CLASS : ''].join(' ').trim()}
          />
        ))}
      </div>

      {/* ── SVG outline overlay ───────────────────────────────────────────── */}
      {isOutlineVisible && (
        <SvgOutlineLayer
          svgString={svgString}
          svgStatus={svgStatus}
          wireListeners={wireListeners}
          containerElRef={svgOutlineContainerRef}
          frameGroupsRef={frameGroupsRef}          
          currentIndexRef={currentIndexRef}
          activeSvgClass={ACTIVE_SVG_CLASS}        
        />
      )}
      </div>

      {/* ── Controls ─────────────────────────────────────────────────────── */}
      <ModelControls
        compassElRef={compassElRef}
        onGoToNorth={handleGoToNorth}
        isOutlineVisible={isOutlineVisible}
        onToggleOutline={() => setIsOutlineVisible(v => !v)}
        availableOutlines={availableOutlines}
        activeOutline={activeOutline}
        onOutlineChange={setActiveOutline}
        activeView={activeView}
        activeProduct={activeProduct}
        hasSiteModel={heroCapabilities.hasSiteModel}
        hasVisualization={heroCapabilities.hasVisualization}
        onBack={handleBack}
        onClose={onClose}
        onRotateLeft={handleRotateLeft}
        onRotateRight={handleRotateRight}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        activeBuilding={activeBuildingIdCad}
        activeBuildingName={activeBuildingName}
        isSingleBuilding={isSingleBuilding}
        isMobile={isMobile}
        isLoading={!isLoaded}
        loadingProgress={loadingProgress}
        colorMode={colorMode}
        onColorModeChange={setColorMode}
        availableColorModes={availableColorModes}
        displayableProducts={displayableProducts}
        legendContainerRef={legendContainerRef}
        colorModeDeciles={colorModeDeciles}
        activeFiltersConfig={activeFiltersConfig}
        defaultFiltersConfig={defaultFiltersConfig}
        setActiveFiltersConfig={setActiveFiltersConfig}
        activeFiltersCount={activeFiltersCount}
        showSoldHighlightNote={showSoldHighlightNote}
        onShowFilters={handleShowFilters}
        onLegendHover={handleLegendHoverChange}
        currentFrameRef={currentIndexRef}
        framesCount={framesCount}
        azimuth={activeAzimuth}
        siteRenderGaussian={siteRenderGaussian}
      />

      {/* ── Loading overlay ───────────────────────────────────────────────── */}
      <LoadingFrames
        progress={loadingProgress}
        isLoaded={isLoaded}
        previewSrc={getFrameUrl(currentFrameForScope)}
      />

      {/* ── Outline preview (building / tier / apartments hover) ───────────────────────── */}
      <PreviewOverlay
        activeTier={null}
        getMobileOutlineCandidates={getMobileOutlineCandidates}
        getMobileProductCandidates={getMobileProductCandidates}
        goToBuildingModel={goToBuildingModel}
        goToTierSection={goToTierSection}
        onRegisterPreviewHandler={handleRegisterPreviewHandler}
        svgContainerRef={svgOutlineContainerRef}
      />
    </div>
  )
}
